import {
  parseCalendarDate,
  parseKnownValue,
  parseKnownValues,
  parseList,
  setOrDelete,
} from "../../UI/Table/listUrlParams.ts";
import { SALES_GATEWAYS } from "./SaleRecord.ts";
import { SalesFilters } from "./salesFilters.ts";
import {
  DEFAULT_SALES_PERIOD_PRESET,
  SALES_COMPARISONS,
  SalesComparison,
  SALES_GRANULARITIES,
  SALES_PERIOD_PRESETS,
  SalesGranularity,
  SalesPeriodPreset,
} from "./salesPeriod.ts";
import {
  SALES_TREND_METRICS,
  SALES_TREND_SPLITS,
  SalesTrendMetric,
  SalesTrendSplit,
} from "./salesTrend.ts";
import { GATEWAY_URL_PARAM } from "./salesStatisticsPath.ts";

export type SalesStatisticsView = {
  preset: SalesPeriodPreset;
  customFrom?: string;
  customTo?: string;
  comparison: SalesComparison;
  granularity?: SalesGranularity;
  filters: SalesFilters;
  metric: SalesTrendMetric;
  split: SalesTrendSplit;
  separatesHeyLightTerms: boolean;
};

const URL_PARAMS = {
  preset: "period",
  customFrom: "from",
  customTo: "to",
  comparison: "compare",
  granularity: "granularity",
  gateways: GATEWAY_URL_PARAM,
  paymentMethods: "method",
  installments: "installments",
  items: "item",
  metric: "metric",
  split: "split",
  separatesHeyLightTerms: "heylightTerms",
};

const FLAG_SET = "1";

export function parseSalesStatisticsView(
  params: URLSearchParams,
): SalesStatisticsView {
  return {
    preset:
      parseKnownValue(params.get(URL_PARAMS.preset), SALES_PERIOD_PRESETS) ??
      DEFAULT_SALES_PERIOD_PRESET,
    customFrom: parseCalendarDate(params.get(URL_PARAMS.customFrom)),
    customTo: parseCalendarDate(params.get(URL_PARAMS.customTo)),
    comparison:
      parseKnownValue(params.get(URL_PARAMS.comparison), SALES_COMPARISONS) ??
      SalesComparison.PreviousPeriod,
    granularity: parseKnownValue(
      params.get(URL_PARAMS.granularity),
      SALES_GRANULARITIES,
    ),
    filters: {
      gateways:
        parseKnownValues(params.get(URL_PARAMS.gateways), SALES_GATEWAYS) ?? [],
      paymentMethods: parseList(params.get(URL_PARAMS.paymentMethods)) ?? [],
      installments: parseList(params.get(URL_PARAMS.installments)) ?? [],
      items: parseList(params.get(URL_PARAMS.items)) ?? [],
    },
    metric:
      parseKnownValue(params.get(URL_PARAMS.metric), SALES_TREND_METRICS) ??
      SalesTrendMetric.Sold,
    split:
      parseKnownValue(params.get(URL_PARAMS.split), SALES_TREND_SPLITS) ??
      SalesTrendSplit.Gateway,
    separatesHeyLightTerms:
      params.get(URL_PARAMS.separatesHeyLightTerms) === FLAG_SET,
  };
}

export function toSalesStatisticsParams(
  view: SalesStatisticsView,
): URLSearchParams {
  const params = new URLSearchParams();
  const isCustom = view.preset === SalesPeriodPreset.Custom;
  setOrDelete(
    params,
    URL_PARAMS.preset,
    view.preset === DEFAULT_SALES_PERIOD_PRESET ? undefined : view.preset,
  );
  setOrDelete(
    params,
    URL_PARAMS.customFrom,
    isCustom ? view.customFrom : undefined,
  );
  setOrDelete(
    params,
    URL_PARAMS.customTo,
    isCustom ? view.customTo : undefined,
  );
  setOrDelete(
    params,
    URL_PARAMS.comparison,
    view.comparison === SalesComparison.PreviousPeriod
      ? undefined
      : view.comparison,
  );
  setOrDelete(params, URL_PARAMS.granularity, view.granularity);
  setOrDelete(params, URL_PARAMS.gateways, view.filters.gateways.join(","));
  setOrDelete(
    params,
    URL_PARAMS.paymentMethods,
    view.filters.paymentMethods.join(","),
  );
  setOrDelete(
    params,
    URL_PARAMS.installments,
    view.filters.installments.join(","),
  );
  setOrDelete(params, URL_PARAMS.items, view.filters.items.join(","));
  setOrDelete(
    params,
    URL_PARAMS.metric,
    view.metric === SalesTrendMetric.Sold ? undefined : view.metric,
  );
  setOrDelete(
    params,
    URL_PARAMS.split,
    view.split === SalesTrendSplit.Gateway ? undefined : view.split,
  );
  setOrDelete(
    params,
    URL_PARAMS.separatesHeyLightTerms,
    view.separatesHeyLightTerms ? FLAG_SET : undefined,
  );
  return params;
}
